import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, endpoints } from "@/lib/api";
import { useAuth } from "@/lib/authContext";
import { toast } from "sonner";
import { ArrowLeft, Megaphone, Disc3, Plus, Trash2, ShieldCheck, Check, X, CalendarPlus } from "lucide-react";
import { formatDate } from "@/lib/format";

/**
 * OrgWorkspace — minimum-viable workspace for Promoter + Label.
 * Sections: Overview / Roster (label) / Events / Team / Verification / Settings.
 * Deliberately lean — Discovery Agent needs to understand the shape, not
 * operate at production depth yet.
 */

const KIND_META = {
    promoter: {
        label: "Promoter",
        Icon: Megaphone,
        showRoster: false,
    },
    label: {
        label: "Record Label",
        Icon: Disc3,
        showRoster: true,
    },
};

export default function OrgWorkspace({ kind: kindProp } = {}) {
    // Route registers each workspace with a fixed kind, but keep param
    // fallback for any future dynamic route. The `kind` param is NOT in
    // the URL for /promoter/:orgId/* and /label/:orgId/* — always take
    // the prop first.
    const params = useParams();
    const kind = kindProp || params.kind;
    const orgId = params.orgId;
    const { user } = useAuth();
    const meta = KIND_META[kind];
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [rosterName, setRosterName] = useState("");
    const [rosterMode, setRosterMode] = useState("");  // "" | "external" | "project_mint"
    const [rosterArtistQuery, setRosterArtistQuery] = useState("");
    const [rosterArtistOptions, setRosterArtistOptions] = useState([]);
    const [rosterBusy, setRosterBusy] = useState(false);
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviteRole, setInviteRole] = useState("team");
    const [inviteBusy, setInviteBusy] = useState(false);

    const [pendingRequests, setPendingRequests] = useState([]);
    const [newEvent, setNewEvent] = useState({ name: "", date: "", venue_id: "", artist_id: "" });
    const [venueOptions, setVenueOptions] = useState([]);
    const [artistOptions, setArtistOptions] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/${kind}-workspace/${orgId}/overview`);
            setData(r.data);
        } catch (e) {
            toast.error("Could not load workspace");
        } finally { setLoading(false); }
        // Pending access requests (Owner/Admin only — quietly ignored otherwise)
        try {
            const d = await endpoints.orgAccessRequests(kind, orgId);
            setPendingRequests(d.requests || []);
        } catch { setPendingRequests([]); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, orgId]);

    const approveRequest = async (req, role) => {
        try {
            await endpoints.orgApproveAccess(kind, orgId, req.id, role);
            toast.success(`Approved ${req.user_email} as ${role}`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Approval failed"); }
    };
    const rejectRequest = async (req) => {
        const reason = window.prompt(`Reason (optional) for rejecting ${req.user_email}:`);
        try {
            await endpoints.orgRejectAccess(kind, orgId, req.id, reason || "");
            toast.success("Request rejected");
            load();
        } catch (e) { toast.error("Reject failed"); }
    };

    const searchVenues = async (q) => {
        if (!q || q.length < 2) { setVenueOptions([]); return; }
        try {
            const d = await endpoints.venueSearch(q);
            setVenueOptions((d?.venues || []).slice(0, 8));
        } catch { setVenueOptions([]); }
    };
    const searchArtists = async (q) => {
        if (!q || q.length < 2) { setArtistOptions([]); return; }
        try {
            const d = await endpoints.artistSearch(q);
            setArtistOptions((d?.artists || []).slice(0, 8));
        } catch { setArtistOptions([]); }
    };

    const createEvent = async () => {
        if (!newEvent.name.trim() || !newEvent.date) {
            toast.error("Event name and date are required");
            return;
        }
        try {
            await endpoints.promoterCreateEvent(orgId, {
                name: newEvent.name.trim(),
                date: newEvent.date,
                venue_id: newEvent.venue_id || null,
                artist_id: newEvent.artist_id || null,
                is_free: true,
                published: false,
            });
            setNewEvent({ name: "", date: "", venue_id: "", artist_id: "" });
            setVenueOptions([]);
            setArtistOptions([]);
            toast.success("Draft event created");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    };

    if (!meta) return <div className="p-10 text-center text-[#666]">Unknown workspace kind</div>;
    const org = data?.[kind];
    const Icon = meta.Icon;
    const verification = org?.verification || { status: "unverified" };
    const isPlatformAdmin = user?.role === "admin";

    const submitVerification = async () => {
        try {
            await api.post(`/${kind}-workspace/${orgId}/verification/submit`);
            toast.success("Submitted for verification");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Submit failed"); }
    };

    const addRosterExternal = async () => {
        if (!rosterName.trim()) return;
        setRosterBusy(true);
        try {
            await api.post(`/label-workspace/${orgId}/roster`, { name: rosterName.trim(), external: true });
            setRosterName("");
            setRosterMode("");
            toast.success("External artist added to roster");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Add failed");
        } finally { setRosterBusy(false); }
    };
    const searchRosterArtists = async (q) => {
        setRosterArtistQuery(q);
        if (!q || q.length < 2) { setRosterArtistOptions([]); return; }
        try {
            const d = await endpoints.artistSearch(q);
            setRosterArtistOptions((d?.artists || []).slice(0, 8));
        } catch { setRosterArtistOptions([]); }
    };
    const addRosterProjectMintArtist = async (artist) => {
        setRosterBusy(true);
        try {
            await api.post(`/label-workspace/${orgId}/roster`, { artist_id: artist.id, external: false });
            setRosterMode("");
            setRosterArtistQuery("");
            setRosterArtistOptions([]);
            toast.success(`${artist.name} added to roster`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Add failed");
        } finally { setRosterBusy(false); }
    };
    const removeRoster = async (entryId) => {
        try {
            await api.delete(`/label-workspace/${orgId}/roster/${entryId}`);
            load();
        } catch { toast.error("Remove failed"); }
    };

    const inviteTeam = async () => {
        const email = inviteEmail.trim();
        if (!email) { toast.error("Enter a teammate's email"); return; }
        setInviteBusy(true);
        try {
            const r = await api.post(`/${kind}-workspace/${orgId}/team`, { email, role: inviteRole });
            const d = r.data || {};
            if (d.already_member) {
                toast.info(`Already a ${d.role} on this workspace`);
            } else {
                toast.success(`Added as ${d.role || inviteRole}`);
            }
            setInviteEmail("");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Add failed");
        } finally { setInviteBusy(false); }
    };

    return (
        <div className="pm-fade min-h-screen" data-testid={`${kind}-workspace`}>
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6">
                <Link to="/account" className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3] hover:text-[#ff4400] inline-flex items-center gap-1">
                    <ArrowLeft className="w-3 h-3" /> Back to account
                </Link>
                <div className="mt-3 flex items-center gap-4">
                    <div className="w-14 h-14 border border-[#2a2a2a] flex items-center justify-center bg-[#141414]">
                        <Icon className="w-6 h-6 text-[#ff4400]" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">{meta.label} workspace</div>
                        <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl truncate" data-testid={`${kind}-name`}>
                            {org?.name || "…"}
                        </h1>
                        <div className="mt-1 text-xs font-mono uppercase text-[#a3a3a3]">
                            {org?.city || "—"}{org?.country ? `, ${org.country}` : ""}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Signed in as</div>
                        <div className="text-xs font-mono truncate max-w-[220px]" data-testid={`${kind}-signed-in-email`}>{user?.email}</div>
                        <div className={`text-[10px] font-mono uppercase mt-0.5 ${isPlatformAdmin ? "text-[#ff9500]" : "text-[#a3a3a3]"}`} data-testid={`${kind}-platform-role`}>
                            {isPlatformAdmin ? "Platform Admin" : "User"}
                        </div>
                    </div>
                </div>
            </header>

            {loading ? (
                <div className="p-10 font-mono text-sm text-[#666]">Loading…</div>
            ) : (
                <div className="px-4 md:px-8 py-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Overview + Verification */}
                    <section className="md:col-span-2 space-y-6">
                        <div className="border border-[#2a2a2a] p-5" data-testid={`${kind}-overview-card`}>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Overview</div>
                            <div className="mt-3 grid grid-cols-3 gap-4">
                                <Stat label="Events" value={data?.events?.length || 0} />
                                <Stat label={meta.showRoster ? "Roster" : "Markets"} value={meta.showRoster ? (data?.roster?.length || 0) : (org?.markets?.length || 0)} />
                                <Stat label="Team" value={data?.members?.length || 0} />
                            </div>
                            <p className="mt-4 text-xs text-[#a3a3a3]" data-testid={`${kind}-description`}>
                                {org?.description || "No description yet. Add one in Settings."}
                            </p>
                        </div>

                        <div className="border border-[#2a2a2a] p-5" data-testid={`${kind}-verification-card`}>
                            <div className="flex items-center justify-between">
                                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Verification</div>
                                <StatusBadge status={verification.status} />
                            </div>
                            {verification.status === "needs_information" && (
                                <div className="mt-3 border border-[#ffea00] p-3 text-xs text-[#ffea00]" data-testid={`${kind}-verification-message`}>
                                    Project Mint needs more info: {verification.admin_message}
                                </div>
                            )}
                            {verification.status === "rejected" && (
                                <div className="mt-3 border border-[#ff3333] p-3 text-xs text-[#ff3333]" data-testid={`${kind}-verification-message`}>
                                    Rejected: {verification.decision_reason}
                                </div>
                            )}
                            {(verification.status === "unverified" || verification.status === "needs_information" || verification.status === "rejected") && (
                                <button
                                    onClick={submitVerification}
                                    className="pm-btn pm-btn-primary mt-4"
                                    data-testid={`${kind}-submit-verification`}
                                >
                                    <ShieldCheck className="w-4 h-4 mr-2" /> Submit for verification
                                </button>
                            )}
                            {verification.status === "pending" && (
                                <div className="mt-3 text-xs font-mono uppercase text-[#ffea00]" data-testid={`${kind}-verification-message`}>
                                    Submitted — Project Mint Admin will review shortly.
                                </div>
                            )}
                        </div>

                        {meta.showRoster && (
                            <div className="border border-[#2a2a2a] p-5" data-testid="label-roster-card">
                                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Roster</div>
                                <p className="mt-1 text-[10px] font-mono uppercase text-[#666]">
                                    Add Project Mint artists (they keep their own workspace) or record
                                    external artists clearly marked <span className="text-[#ff9500]">Not on Project Mint</span>.
                                </p>
                                {rosterMode === "" && (
                                    <div className="mt-3 flex flex-wrap gap-2" data-testid="label-roster-actions">
                                        <button onClick={() => setRosterMode("project_mint")} className="pm-btn pm-btn-primary text-[10px]" data-testid="label-roster-add-pm-btn">
                                            <Plus className="w-3 h-3 mr-1" /> Add Project Mint artist
                                        </button>
                                        <button onClick={() => setRosterMode("external")} className="pm-btn text-[10px]" data-testid="label-roster-add-external-btn">
                                            <Plus className="w-3 h-3 mr-1" /> Add external artist
                                        </button>
                                    </div>
                                )}
                                {rosterMode === "project_mint" && (
                                    <div className="mt-3 space-y-2 border border-dashed border-[#2a2a2a] p-3" data-testid="label-roster-pm-form">
                                        <div className="flex items-center justify-between">
                                            <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">Add Project Mint artist</div>
                                            <button onClick={() => { setRosterMode(""); setRosterArtistQuery(""); setRosterArtistOptions([]); }} className="text-[10px] font-mono uppercase text-[#666] hover:text-[#ff3333]">Cancel</button>
                                        </div>
                                        <input
                                            value={rosterArtistQuery}
                                            onChange={(e) => searchRosterArtists(e.target.value)}
                                            placeholder="Search Project Mint artists by name…"
                                            className="w-full bg-transparent border-b border-[#2a2a2a] focus:border-[#ff4400] py-2 outline-none text-sm"
                                            data-testid="label-roster-pm-search"
                                        />
                                        {rosterArtistQuery.length >= 2 && rosterArtistOptions.length === 0 && (
                                            <div className="text-[10px] font-mono uppercase text-[#666]" data-testid="label-roster-pm-empty">
                                                No Project Mint artist matches "{rosterArtistQuery}". Use "Add external artist" instead.
                                            </div>
                                        )}
                                        <ul className="max-h-56 overflow-y-auto divide-y divide-[#2a2a2a]">
                                            {rosterArtistOptions.map((a) => (
                                                <li key={a.id} className="flex items-center gap-2 py-2" data-testid={`label-roster-pm-option-${a.id}`}>
                                                    <div className="w-8 h-8 border border-[#2a2a2a] bg-[#141414] flex items-center justify-center text-[10px] font-mono uppercase text-[#ff4400] shrink-0">
                                                        {(a.name || "?").charAt(0)}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-sm font-display font-bold uppercase truncate">{a.name}</div>
                                                        <div className="text-[10px] font-mono uppercase text-[#666] truncate">{a.city || "—"}{a.country ? `, ${a.country}` : ""}</div>
                                                    </div>
                                                    <button onClick={() => addRosterProjectMintArtist(a)} disabled={rosterBusy} className="pm-btn pm-btn-primary text-[10px] shrink-0" data-testid={`label-roster-pm-add-${a.id}`}>
                                                        {rosterBusy ? "Adding…" : "Add to roster"}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                        <p className="text-[10px] font-mono uppercase text-[#666]">
                                            Artist ownership isn't transferred to the label. They stay in control of their profile.
                                        </p>
                                    </div>
                                )}
                                {rosterMode === "external" && (
                                    <div className="mt-3 space-y-2 border border-dashed border-[#2a2a2a] p-3" data-testid="label-roster-external-form">
                                        <div className="flex items-center justify-between">
                                            <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">Add external artist</div>
                                            <button onClick={() => { setRosterMode(""); setRosterName(""); }} className="text-[10px] font-mono uppercase text-[#666] hover:text-[#ff3333]">Cancel</button>
                                        </div>
                                        <input
                                            value={rosterName}
                                            onChange={(e) => setRosterName(e.target.value)}
                                            placeholder="Artist name (not on Project Mint yet)"
                                            className="w-full bg-transparent border-b border-[#2a2a2a] focus:border-[#ff4400] py-2 outline-none text-sm"
                                            data-testid="label-roster-external-name"
                                        />
                                        <div className="flex justify-end">
                                            <button onClick={addRosterExternal} disabled={rosterBusy || !rosterName.trim()} className="pm-btn pm-btn-primary text-[10px]" data-testid="label-roster-external-add-btn">
                                                {rosterBusy ? "Adding…" : "Add external artist"}
                                            </button>
                                        </div>
                                        <p className="text-[10px] font-mono uppercase text-[#666]">
                                            This creates a roster placeholder marked "Not on Project Mint". No workspace is created.
                                        </p>
                                    </div>
                                )}
                                <ul className="mt-4 space-y-2" data-testid="label-roster-list">
                                    {(data?.roster || []).length === 0 ? (
                                        <li className="text-[10px] font-mono uppercase text-[#666]">No roster yet.</li>
                                    ) : (data?.roster || []).map((r) => (
                                        <li key={r.id} className="flex items-center gap-3 border border-[#2a2a2a] p-2" data-testid={`label-roster-entry-${r.id}`}>
                                            <div className="w-8 h-8 border border-[#2a2a2a] flex items-center justify-center text-[10px] font-mono uppercase text-[#ff4400] shrink-0">
                                                {(r.artist_name || "?").charAt(0)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-display font-bold uppercase truncate">{r.artist_name}</div>
                                                {r.external ? (
                                                    <div className="text-[10px] font-mono uppercase text-[#ff9500]" data-testid={`label-roster-external-${r.id}`}>Not on Project Mint · Unclaimed</div>
                                                ) : (
                                                    <div className="text-[10px] font-mono uppercase text-[#00e676]" data-testid={`label-roster-pm-${r.id}`}>Project Mint artist</div>
                                                )}
                                            </div>
                                            {!r.external && r.artist_id && (
                                                <Link to={`/artists/${r.artist_id}`} className="pm-btn text-[10px] shrink-0" data-testid={`label-roster-open-${r.id}`}>
                                                    View
                                                </Link>
                                            )}
                                            <button onClick={() => removeRoster(r.id)} className="pm-btn text-[10px] shrink-0" data-testid={`label-roster-remove-${r.id}`}>
                                                <Trash2 className="w-3 h-3" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="border border-[#2a2a2a] p-5" data-testid={`${kind}-events-card`}>
                            <div className="flex items-center justify-between">
                                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Events</div>
                                {kind === "promoter" && <span className="text-[10px] font-mono uppercase text-[#666]">Artist attachment optional</span>}
                            </div>
                            {kind === "promoter" && (
                                <div className="mt-3 border border-dashed border-[#2a2a2a] p-3 space-y-2" data-testid="promoter-new-event-card">
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">New event</div>
                                    <input
                                        value={newEvent.name}
                                        onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                                        placeholder="Event name (e.g. DJ Night, Showcase, Festival)"
                                        className="w-full bg-transparent border-b border-[#2a2a2a] focus:border-[#ff4400] py-2 outline-none text-sm"
                                        data-testid="promoter-new-event-name"
                                    />
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        <input
                                            type="date"
                                            value={newEvent.date}
                                            onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                                            className="bg-transparent border-b border-[#2a2a2a] focus:border-[#ff4400] py-2 outline-none text-sm"
                                            data-testid="promoter-new-event-date"
                                        />
                                        <div className="relative">
                                            <input
                                                placeholder="Venue (search Project Mint)"
                                                onChange={(e) => searchVenues(e.target.value)}
                                                className="w-full bg-transparent border-b border-[#2a2a2a] focus:border-[#ff4400] py-2 outline-none text-sm"
                                                data-testid="promoter-new-event-venue-search"
                                            />
                                            {venueOptions.length > 0 && (
                                                <ul className="absolute left-0 right-0 bg-[#141414] border border-[#2a2a2a] max-h-40 overflow-y-auto z-10" data-testid="promoter-venue-options">
                                                    {venueOptions.map((v) => (
                                                        <li
                                                            key={v.id}
                                                            onClick={() => { setNewEvent({ ...newEvent, venue_id: v.id }); setVenueOptions([]); toast.success(`Selected ${v.name}`); }}
                                                            className="px-3 py-1 hover:bg-[#2a2a2a] cursor-pointer text-xs font-mono uppercase truncate"
                                                            data-testid={`promoter-venue-option-${v.id}`}
                                                        >
                                                            {v.name} · {v.city}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    </div>
                                    <div className="relative">
                                        <input
                                            placeholder="Artist (optional — leave empty for DJ nights / festivals)"
                                            onChange={(e) => searchArtists(e.target.value)}
                                            className="w-full bg-transparent border-b border-[#2a2a2a] focus:border-[#ff4400] py-2 outline-none text-sm"
                                            data-testid="promoter-new-event-artist-search"
                                        />
                                        {artistOptions.length > 0 && (
                                            <ul className="absolute left-0 right-0 bg-[#141414] border border-[#2a2a2a] max-h-40 overflow-y-auto z-10" data-testid="promoter-artist-options">
                                                {artistOptions.map((a) => (
                                                    <li
                                                        key={a.id}
                                                        onClick={() => { setNewEvent({ ...newEvent, artist_id: a.id }); setArtistOptions([]); toast.success(`Attached ${a.name}`); }}
                                                        className="px-3 py-1 hover:bg-[#2a2a2a] cursor-pointer text-xs font-mono uppercase truncate"
                                                        data-testid={`promoter-artist-option-${a.id}`}
                                                    >
                                                        {a.name}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="text-[10px] font-mono uppercase text-[#666]">
                                            {newEvent.venue_id ? "✓ Venue selected" : "No venue yet"}
                                            {" · "}
                                            {newEvent.artist_id ? "✓ Artist attached" : "No artist (OK)"}
                                        </div>
                                        <button onClick={createEvent} className="pm-btn pm-btn-primary text-[10px]" data-testid="promoter-new-event-create">
                                            <CalendarPlus className="w-3 h-3 mr-1" /> Create draft event
                                        </button>
                                    </div>
                                </div>
                            )}
                            {(data?.events || []).length === 0 ? (
                                <div className="mt-3 text-[10px] font-mono uppercase text-[#666]">
                                    No events yet. Events attached to this {meta.label.toLowerCase()} will appear here.
                                </div>
                            ) : (
                                <ul className="mt-3 space-y-2">
                                    {data.events.map((e) => (
                                        <li key={e.id} className="flex items-center justify-between border border-[#2a2a2a] p-2" data-testid={`${kind}-event-${e.id}`}>
                                            <div className="min-w-0">
                                                <div className="text-sm font-display font-bold uppercase truncate">{e.name}</div>
                                                <div className="text-[10px] font-mono uppercase text-[#666]">
                                                    {formatDate(e.date)}
                                                    {e.venue_name ? ` · ${e.venue_name}` : ""}
                                                    {e.artist_name ? ` · ${e.artist_name}` : " · No artist"}
                                                    {e.status === "draft" ? " · DRAFT" : ""}
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {pendingRequests.length > 0 && (
                            <div className="border border-[#ff9500] p-5" data-testid={`${kind}-access-requests-card`}>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff9500]">Access requests ({pendingRequests.length})</div>
                                <ul className="mt-3 space-y-2">
                                    {pendingRequests.map((req) => (
                                        <li key={req.id} className="flex flex-col md:flex-row md:items-center gap-2 border border-[#2a2a2a] p-2" data-testid={`${kind}-access-request-${req.id}`}>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-mono truncate">{req.user_email}</div>
                                                <div className="text-[10px] font-mono uppercase text-[#666] truncate">
                                                    Wants: <span className="text-[#ff4400]">{req.relationship}</span>
                                                    {req.message ? ` · "${req.message}"` : ""}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                {kind === "venue" && (
                                                    <>
                                                        <button onClick={() => approveRequest(req, "manager")} className="pm-btn pm-btn-primary text-[10px]" data-testid={`${kind}-approve-manager-${req.id}`}>
                                                            <Check className="w-3 h-3 mr-1" /> Manager
                                                        </button>
                                                        <button onClick={() => approveRequest(req, "staff")} className="pm-btn text-[10px]" data-testid={`${kind}-approve-staff-${req.id}`}>Staff</button>
                                                    </>
                                                )}
                                                {kind === "promoter" && (
                                                    <>
                                                        <button onClick={() => approveRequest(req, "admin")} className="pm-btn pm-btn-primary text-[10px]" data-testid={`${kind}-approve-admin-${req.id}`}>
                                                            <Check className="w-3 h-3 mr-1" /> Admin
                                                        </button>
                                                        <button onClick={() => approveRequest(req, "team")} className="pm-btn text-[10px]" data-testid={`${kind}-approve-team-${req.id}`}>Team</button>
                                                    </>
                                                )}
                                                {kind === "label" && (
                                                    <>
                                                        <button onClick={() => approveRequest(req, "admin")} className="pm-btn pm-btn-primary text-[10px]" data-testid={`${kind}-approve-admin-${req.id}`}>
                                                            <Check className="w-3 h-3 mr-1" /> Admin
                                                        </button>
                                                        <button onClick={() => approveRequest(req, "team")} className="pm-btn text-[10px]" data-testid={`${kind}-approve-team-${req.id}`}>Team</button>
                                                    </>
                                                )}
                                                <button onClick={() => rejectRequest(req)} className="pm-btn text-[10px] text-[#ff3333] border-[#ff3333]" data-testid={`${kind}-reject-${req.id}`}>
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </section>

                    {/* Team + Settings */}
                    <aside className="space-y-6">
                        <div className="border border-[#2a2a2a] p-5" data-testid={`${kind}-team-card`}>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Team</div>
                            <ul className="mt-3 space-y-2" data-testid={`${kind}-team-list`}>
                                {(data?.members || []).map((m) => (
                                    <li key={m.id || m.user_id} className="flex items-center gap-2 text-xs font-mono" data-testid={`${kind}-team-member-${m.user_id}`}>
                                        <span className="text-[#ff4400] uppercase">{m.role}</span>
                                        <span className="text-[#a3a3a3] truncate">{m.user_id === user?.id ? "You" : m.user_id}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-4 space-y-2">
                                <input
                                    value={inviteEmail}
                                    onChange={(e) => setInviteEmail(e.target.value)}
                                    placeholder="teammate@email.com"
                                    className="w-full bg-transparent border-b border-[#2a2a2a] focus:border-[#ff4400] py-2 outline-none text-sm"
                                    data-testid={`${kind}-team-email-input`}
                                />
                                <select
                                    value={inviteRole}
                                    onChange={(e) => setInviteRole(e.target.value)}
                                    className="w-full bg-transparent border border-[#2a2a2a] px-2 py-1 font-mono uppercase text-xs"
                                    data-testid={`${kind}-team-role-select`}
                                >
                                    <option value="admin" className="bg-[#141414]">Admin</option>
                                    <option value="team" className="bg-[#141414]">Team</option>
                                    {kind === "promoter" && <option value="staff" className="bg-[#141414]">Event staff</option>}
                                </select>
                                <button onClick={inviteTeam} disabled={inviteBusy} className="pm-btn w-full" data-testid={`${kind}-team-add-btn`}>
                                    {inviteBusy ? <>Adding…</> : <><Plus className="w-4 h-4 mr-1" /> Add teammate</>}
                                </button>
                                <div className="text-[10px] font-mono uppercase text-[#666]">
                                    {kind === "label" ? (
                                        <>Admin: manage roster, events, and team. Team: view + operate roster/events.</>
                                    ) : kind === "promoter" ? (
                                        <>Admin: manage events, team, roster. Team: work with authorized events.</>
                                    ) : (
                                        <>Admin: manage workspace + team. Team: authorized operations only.</>
                                    )}
                                </div>
                                <div className="text-[10px] font-mono uppercase text-[#666]">
                                    Teammates must already have a Project Mint account.
                                </div>
                            </div>
                        </div>

                        <div className="border border-[#2a2a2a] p-5" data-testid={`${kind}-quick-links-card`}>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-[#666] mb-2">Quick links</div>
                            <Link to={`/${kind}s/${orgId}`} className="block text-xs font-mono uppercase text-[#ff4400] hover:underline mt-1" data-testid={`${kind}-view-public-link`}>
                                View public page →
                            </Link>
                            <Link to="/account" className="block text-xs font-mono uppercase text-[#a3a3a3] hover:text-[#ff4400] mt-1">
                                Back to account
                            </Link>
                        </div>
                    </aside>
                </div>
            )}
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">{label}</div>
            <div className="mt-1 font-display font-black uppercase text-3xl">{value}</div>
        </div>
    );
}

function StatusBadge({ status }) {
    const map = {
        unverified: ["Unverified", "border-[#666] text-[#a3a3a3]"],
        pending: ["Pending", "border-[#ffea00] text-[#ffea00]"],
        needs_information: ["Needs info", "border-[#ffea00] text-[#ffea00]"],
        verified: ["Verified", "border-[#00e676] text-[#00e676]"],
        rejected: ["Rejected", "border-[#ff3333] text-[#ff3333]"],
    };
    const [label, cls] = map[status] || map.unverified;
    return (
        <span className={`text-[10px] font-mono uppercase px-2 py-0.5 border ${cls}`} data-testid="org-verification-status">
            {label}
        </span>
    );
}
